import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing page meta tags
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} canonicalUrl - Canonical URL (optional)
 * @param {string} ogImage - Open Graph image URL (optional)
 * @param {string} ogType - Open Graph type (default: website)
 * @param {boolean} noIndex - Whether to add noindex directive (default: false)
 * @param {string|string[]} keywords - Topical keywords for AI/search discoverability (optional)
 */
const SEO = ({
  title,
  description,
  canonicalUrl,
  ogImage = '/Assets/images/og-default.jpg',
  ogType = 'website',
  noIndex = false,
  keywords
}) => {
  const siteName = 'Portico Systems';
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} | Commercial Flooring Solutions`;
  const baseUrl = 'https://porticosystems.com';
  const keywordsContent = Array.isArray(keywords) ? keywords.join(', ') : keywords;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywordsContent && <meta name="keywords" content={keywordsContent} />}
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={`${baseUrl}${canonicalUrl}`} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={siteName} />
      {ogImage && <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />}
      {canonicalUrl && <meta property="og:url" content={`${baseUrl}${canonicalUrl}`} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />}
    </Helmet>
  );
};

export default SEO;
